import {
  useContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaRegArrowAltCircleUp,
  FaPlus,
  FaFileDownload,
  FaFilter,
} from 'react-icons/fa';
import { FiFileText } from 'react-icons/fi';
import { MdDelete, MdMoreHoriz } from 'react-icons/md';
import { Link } from 'react-router-dom';
import AuthenticateContext from '../../provider/context/authenticate.context';
import { Button, IconButton, ButtonGroup } from '../../components/button';
import Dropdown, {
  DropdownItem,
  DropdownButtonOption,
  DropdownLinkOption,
  DropdownDivOption,
} from '../../components/dropdown';
import { Heading1 } from '../../components/heading';
import { StateModal, TitleModal } from '../../components/modal';
import {
  Section,
  SectionBody,
  SectionHeader,
  SectionToolbar,
  SectionToolbarItem,
} from '../../components/section';
import { Table } from '../../components/table';
import { useLimitChange, usePageChange } from '../../components/table/hooks';
import { showSuccessMessage } from '../../module/message';
import {
  batchDeleteTerminal,
  deleteTerminal,
  getTerminalList,
  exportTerminals,
} from '../../module/terminal';
import { getBottlerSimpleList } from '../../module/bottler';
import { getSalesCenterSimpleList } from '../../module/salesCenter';
import { downloadFile } from '../../module/download';
import handleApiResponse from '../../utils/api/handleApiResponse';
import { Grid, Column } from '../../components/grid';
import {
  FormItem,
  Input,
  Label,
  Checkbox,
  Searchbox,
} from '../../components/form';
import { TiArrowSortedDown } from 'react-icons/ti';
import { BadgeFill } from '../../components/badge';
import { RxCross1 } from 'react-icons/rx';

const TerminalList = () => {
  const { t } = useTranslation();
  const { userInformation } = useContext(AuthenticateContext);
  const [selectedTerminal, setSelectedTerminal] = useState([]);
  const [isSelectedAll, setIsSelectedAll] = useState(false);
  const [listData, setListData] = useState([]);
  const [listParams, setListParams] = useState({
    page: 1,
    limit: 10,
    filters: { outletName: '', nameExtension: '' },
  });
  const [total, setTotal] = useState(1);
  const [bottlerList, setBottlerList] = useState({
    data: [],
    currentPage: 1,
    lastPage: 1,
  });
  const [bottlerSimpleList, setBottlerSimpleList] = useState([]);
  const [fetchingSalesCenterList, setFetchingSalesCenterList] = useState(false);
  const [salesCenterList, setSalesCenterList] = useState({
    data: [],
    currentPage: 1,
    lastPage: 1,
  });
  const [salesCenterSimpleList, setSalesCenterSimpleList] = useState([]);
  const [snOptions, setSnOptions] = useState([]);
  const [bottlerOptions, setBottlerOptions] = useState([]);
  const [salesCenterOptions, setSalesCenterOptions] = useState([]);
  const [outletNumOptions, setOutletNumOptions] = useState([]);
  const [assetNumOptions, setAssetNumOptions] = useState([]);

  const onLimitChange = useLimitChange(setListParams);
  const onPageChange = usePageChange(setListParams);

  const checkedSN = useMemo(() => {
    return snOptions.filter((option) => {
      return option.checked;
    });
  }, [snOptions]);

  const checkedBottlers = useMemo(() => {
    return bottlerOptions.filter((option) => {
      return option.checked;
    });
  }, [bottlerOptions]);

  const checkedBottlersId = useMemo(() => {
    return checkedBottlers.map((option) => {
      return option.id;
    });
  }, [checkedBottlers]);

  const checkedSalesCenter = useMemo(() => {
    return salesCenterOptions.filter((option) => {
      return option.checked;
    });
  }, [salesCenterOptions]);

  const checkedSalesCenterId = useMemo(() => {
    return checkedSalesCenter.map((option) => {
      return option.id;
    });
  }, [checkedSalesCenter]);

  const checkedOutletNum = useMemo(() => {
    return outletNumOptions.filter((option) => {
      return option.checked;
    });
  }, [outletNumOptions]);

  const checkedAssetNum = useMemo(() => {
    return assetNumOptions.filter((option) => {
      return option.checked;
    });
  }, [assetNumOptions]);

  const isAnyChecked =
    checkedSN.length > 0 ||
    checkedBottlers.length > 0 ||
    checkedSalesCenter.length > 0 ||
    checkedOutletNum.length > 0 ||
    checkedAssetNum.length > 0;

  const handleGetBottlerList = useCallback((currentPage) => {
    const parameters = {
      page: currentPage,
      limit: 100,
    };
    handleApiResponse(getBottlerSimpleList(parameters), (response) => {
      const { list, total } = response.data.data;

      setBottlerList((previous) => {
        const newBottlerList = [...previous.data, ...list];

        return {
          data: newBottlerList,
          currentPage: currentPage + 1,
          lastPage: Math.ceil(total / 100),
        };
      });
    });
  }, []);

  const handleSelectedChange = (id) => {
    setSelectedTerminal((previous) => {
      const newSelectedTerminal = [...previous];
      const index = previous.indexOf(id);
      if (index === -1) {
        newSelectedTerminal.push(id);
      } else {
        newSelectedTerminal.splice(index, 1);
      }

      return newSelectedTerminal;
    });
  };

  const handleSelectAll = useCallback(() => {
    if (isSelectedAll) {
      setSelectedTerminal([]);
    } else {
      const newSelectedTerminal = [];
      listData.forEach((bottlerGroup) => {
        newSelectedTerminal.push(bottlerGroup.id);
      });
      setSelectedTerminal(newSelectedTerminal);
    }

    setIsSelectedAll(!isSelectedAll);
  }, [isSelectedAll, listData]);

  const handleGetTerminalList = useCallback(() => {
    const requestData = {
      page: listParams.page,
      limit: listParams.limit,
    };
    if (checkedSN.length > 0) {
      requestData.sn = checkedSN
        .map((item) => {
          return item.text;
        })
        .join(',');
    }
    if (checkedBottlersId.length > 0) {
      requestData.bottlerId = checkedBottlersId.join(',');
    }
    if (checkedSalesCenterId.length > 0) {
      requestData.saleCenterId = checkedSalesCenterId.join(',');
    }
    if (checkedOutletNum.length > 0) {
      requestData.outletNumber = checkedOutletNum
        .map((item) => {
          return item.text;
        })
        .join(',');
    }
    if (checkedAssetNum.length > 0) {
      requestData.assetNumber = checkedAssetNum
        .map((item) => {
          return item.text;
        })
        .join(',');
    }
    if (listParams.filters.outletName.trim()) {
      requestData.outletName = listParams.filters.outletName.trim();
    }
    if (listParams.filters.nameExtension.trim()) {
      requestData.nameExtension = listParams.filters.nameExtension.trim();
    }

    handleApiResponse(getTerminalList(requestData), (response) => {
      const { total, list } = response.data.data;
      setTotal(total);
      setListData(list);
      setSelectedTerminal([]);
    });
  }, [
    listParams,
    checkedSN,
    checkedOutletNum,
    checkedBottlersId,
    checkedSalesCenterId,
    checkedAssetNum,
  ]);

  const handleBatchDeleteTerminal = (ids) => {
    handleApiResponse(batchDeleteTerminal(ids), () => {
      showSuccessMessage({ message: t('success:RemoveTerminal') });
      handleGetTerminalList();
    });
  };

  const handleDeleteTerminal = useCallback(
    (id) => {
      handleApiResponse(deleteTerminal({ id }), () => {
        showSuccessMessage({ message: t('success:RemoveTerminal') });
        handleGetTerminalList();
      });
    },
    [t, handleGetTerminalList],
  );

  const handleBatchRemove = (ids) => {
    return StateModal({
      type: 'warning',
      title: t('AreYouSure'),
      text: t('DoYouWantToRemoveTheseTerminals'),
      showCancelButton: true,
      confirmButtonText: t('button:Confirm'),
      cancelButtonText: t('button:Cancel'),
      allowDismiss: true,
      onConfirm() {
        handleBatchDeleteTerminal(ids);
      },
    });
  };

  const handleRemove = useCallback(
    (id) => {
      return StateModal({
        type: 'warning',
        title: t('AreYouSure'),
        text: t('DoYouWantToRemoveThisTerminal'),
        showCancelButton: true,
        confirmButtonText: t('button:Confirm'),
        cancelButtonText: t('button:Cancel'),
        allowDismiss: true,
        onConfirm() {
          handleDeleteTerminal(id);
        },
      });
    },
    [t, handleDeleteTerminal],
  );

  const generateActionDropdown = useCallback(
    ({ id }) => {
      return (
        <Dropdown>
          <Dropdown.Toggle>
            <IconButton>
              <MdMoreHoriz />
            </IconButton>
          </Dropdown.Toggle>

          <Dropdown.Content>
            <DropdownItem>
              <DropdownLinkOption to={`/terminal/${id}`}>
                <FiFileText />
                {t('button:Details')}
              </DropdownLinkOption>
            </DropdownItem>
            {userInformation.permissions.includes('terminal.delete') && (
              <DropdownItem onClick={() => handleRemove(id)}>
                <DropdownButtonOption>
                  <MdDelete />
                  {t('button:Remove')}
                </DropdownButtonOption>
              </DropdownItem>
            )}
          </Dropdown.Content>
        </Dropdown>
      );
    },
    [handleRemove, t, userInformation.permissions],
  );

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: t('HardwareSerialNumber'),
        fieldName: 'sn',
      },
      {
        title: t('Bottler'),
        fieldName: 'bottlerName',
      },
      {
        title: t('SalesCenter'),
        fieldName: 'saleCenterName',
      },
      {
        title: t('OutletNumber'),
        fieldName: 'outletNumber',
      },
      {
        title: t('OutletName'),
        fieldName: 'outletName',
      },
      {
        title: t('NameExtension'),
        fieldName: 'nameExtension',
      },
      {
        title: t('AssetNumber'),
        fieldName: 'assetNumber',
      },
      {
        title: t('CreatedTime'),
        fieldName: 'createdAt',
      },
      {
        title: t('Action'),
        custom: true,
        width: '10%',
        align: 'center',
        render(data) {
          return generateActionDropdown(data);
        },
      },
    ];

    if (userInformation.permissions.includes('terminal.delete')) {
      columns.unshift({
        title: (
          <Checkbox
            noMargin
            onChange={handleSelectAll}
            checked={isSelectedAll}
          />
        ),
        fieldName: 'id',
        sortable: false,
        width: '60px',
        render(data) {
          return (
            <Checkbox
              noMargin
              onChange={() => {
                handleSelectedChange(data);
              }}
              checked={selectedTerminal.includes(data)}
            />
          );
        },
      });
    }

    return columns;
  }, [
    t,
    generateActionDropdown,
    userInformation.permissions,
    handleSelectAll,
    isSelectedAll,
    selectedTerminal,
  ]);

  const handleExportList = () => {
    const requestData = {};
    if (checkedSN.length > 0) {
      requestData.sn = checkedSN
        .map((item) => {
          return item.text;
        })
        .join(',');
    }
    if (checkedBottlersId.length > 0) {
      requestData.bottlerId = checkedBottlersId;
    }
    if (checkedSalesCenterId.length > 0) {
      requestData.saleCenterId = checkedSalesCenterId;
    }
    if (checkedOutletNum.length > 0) {
      requestData.outletNumber = checkedOutletNum
        .map((item) => {
          return item.text;
        })
        .join(',');
    }
    if (checkedAssetNum.length > 0) {
      requestData.assetNumber = checkedAssetNum
        .map((item) => {
          return item.text;
        })
        .join(',');
    }
    if (listParams.filters.outletName.trim()) {
      requestData.outletName = listParams.filters.outletName.trim();
    }
    if (listParams.filters.nameExtension.trim()) {
      requestData.nameExtension = listParams.filters.nameExtension.trim();
    }

    handleApiResponse(exportTerminals(requestData), (response) => {
      const { url } = response.data.data;

      if (url) {
        handleApiResponse(downloadFile(url), (response) => {
          const urlSegment = url.split('/');
          const fileName = urlSegment[urlSegment.length - 1];
          const href = URL.createObjectURL(response.data);
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        });
      }
    });
  };

  const showFilterPanel = () => {
    TitleModal({
      size: 'normal',
      children: (
        <TerminalListFilter
          bottlerSimpleList={bottlerSimpleList}
          listParameters={listParams}
          setListParameters={setListParams}
        />
      ),
    });
  };

  const handleGetSalesCenterList = useCallback((bottlerId, currentPage) => {
    const parameters = {
      page: currentPage,
    };
    handleApiResponse(
      getSalesCenterSimpleList(bottlerId, parameters),
      (response) => {
        const { list, limit, total } = response.data.data;

        setSalesCenterList((previous) => {
          const newSalesCenterList = [...previous.data, ...list];

          return {
            data: newSalesCenterList,
            currentPage: currentPage + 1,
            lastPage: Math.ceil(total / limit),
          };
        });
        setFetchingSalesCenterList(false);
      },
    );
  }, []);

  const handleToggleCheckbox = (index, state, setState) => {
    const newOptions = [...state];
    newOptions[index].checked = !newOptions[index].checked;
    setState(newOptions);
  };

  const handleReset = () => {
    setSnOptions([]);
    setBottlerOptions(() => {
      return bottlerSimpleList.map((item) => {
        return { ...item, checked: false };
      });
    });
    setSalesCenterOptions([]);
    setOutletNumOptions([]);
    setAssetNumOptions([]);
  };

  useEffect(() => {
    if (bottlerList.currentPage <= bottlerList.lastPage) {
      handleGetBottlerList(bottlerList.currentPage);
    } else {
      setBottlerSimpleList(
        bottlerList.data.map((bottler) => {
          return { id: bottler.id, text: bottler.name, checked: false };
        }),
      );
    }
  }, [
    handleGetBottlerList,
    bottlerList.data,
    bottlerList.currentPage,
    bottlerList.lastPage,
  ]);

  useEffect(() => {
    if (bottlerSimpleList.length === 0) return;
    setBottlerOptions([...bottlerSimpleList]);
  }, [bottlerSimpleList]);

  useEffect(() => {
    handleGetTerminalList();
  }, [handleGetTerminalList]);

  useEffect(() => {
    if (listData.length !== 0 && selectedTerminal.length === listData.length) {
      setIsSelectedAll(true);
    } else {
      setIsSelectedAll(false);
    }
  }, [listData, selectedTerminal]);

  useEffect(() => {
    if (checkedBottlersId.length === 0) return;
    if (fetchingSalesCenterList) return;

    if (salesCenterList.currentPage <= salesCenterList.lastPage) {
      handleGetSalesCenterList(
        checkedBottlersId.join(','),
        salesCenterList.currentPage,
      );
      setFetchingSalesCenterList(true);
    } else {
      setSalesCenterSimpleList(
        salesCenterList.data.map((salesCenter) => {
          return { id: salesCenter.id, text: salesCenter.name, checked: false };
        }),
      );
    }
  }, [
    fetchingSalesCenterList,
    handleGetSalesCenterList,
    salesCenterList.data,
    salesCenterList.currentPage,
    salesCenterList.lastPage,
    checkedBottlersId,
  ]);

  useEffect(() => {
    if (salesCenterSimpleList.length === 0) return;
    setSalesCenterOptions([...salesCenterSimpleList]);
  }, [salesCenterSimpleList]);

  // reset sales center related state
  useEffect(() => {
    setSalesCenterList({
      data: [],
      currentPage: 1,
      lastPage: 1,
    });
    setSalesCenterSimpleList([]);
    setSalesCenterOptions([]);
  }, [checkedBottlersId]);

  return (
    <>
      <Section noPadding>
        <SectionHeader sticky backgroundFill>
          <SectionToolbar>
            <SectionToolbarItem>
              <Heading1>{t('TerminalList')}</Heading1>
            </SectionToolbarItem>
            <SectionToolbarItem>
              {userInformation.permissions.includes('terminal.delete') &&
                selectedTerminal.length > 0 && (
                  <Button
                    secondary
                    onClick={() => handleBatchRemove(selectedTerminal)}
                  >
                    <MdDelete />
                    {t('Remove')}
                  </Button>
                )}
              {userInformation.permissions.includes('terminal.create') && (
                <>
                  <Link to='/terminal/import'>
                    <Button>
                      <FaRegArrowAltCircleUp />
                      {t('button:Import')}
                    </Button>
                  </Link>
                  <Link to='/terminal/create'>
                    <Button>
                      <FaPlus />
                      {t('button:Create')}
                    </Button>
                  </Link>
                </>
              )}

              <Dropdown fixed>
                <Dropdown.Toggle>
                  <Button secondary>
                    <FaFileDownload />
                    {t('button:Export')}
                  </Button>
                </Dropdown.Toggle>
                <Dropdown.Content>
                  <DropdownItem onClick={handleExportList}>
                    <DropdownButtonOption>
                      {t('TerminalList')}
                    </DropdownButtonOption>
                  </DropdownItem>
                </Dropdown.Content>
              </Dropdown>

              <Button secondary onClick={showFilterPanel}>
                <FaFilter />
                {t('Filter')}
              </Button>
            </SectionToolbarItem>
          </SectionToolbar>
          <SectionToolbar>
            <SectionToolbarItem>
              <InputDropdown
                title='HardwareSerialNumber'
                checked={checkedSN}
                options={snOptions}
                setOptions={setSnOptions}
                toggleCheckbox={handleToggleCheckbox}
              />

              <SearchDropdown
                title='Bottler'
                checked={checkedBottlers}
                simpleList={bottlerSimpleList}
                options={bottlerOptions}
                setOptions={setBottlerOptions}
                toggleCheckbox={handleToggleCheckbox}
              />

              <SearchDropdown
                title='SalesCenter'
                checked={checkedSalesCenter}
                simpleList={salesCenterSimpleList}
                options={salesCenterOptions}
                setOptions={setSalesCenterOptions}
                toggleCheckbox={handleToggleCheckbox}
              />

              <InputDropdown
                title='OutletNumber'
                checked={checkedOutletNum}
                options={outletNumOptions}
                setOptions={setOutletNumOptions}
                toggleCheckbox={handleToggleCheckbox}
              />

              <InputDropdown
                title='AssetNumber'
                checked={checkedAssetNum}
                options={assetNumOptions}
                setOptions={setAssetNumOptions}
                toggleCheckbox={handleToggleCheckbox}
              />

              {isAnyChecked && (
                <div>
                  <Button danger onClick={handleReset}>
                    {t('button:Reset')}
                  </Button>
                </div>
              )}
            </SectionToolbarItem>
          </SectionToolbar>
        </SectionHeader>
        <SectionBody>
          <Table
            columns={tableColumns}
            data={listData}
            currentPage={listParams.page}
            limit={listParams.limit}
            total={total}
            onLimitChange={onLimitChange}
            onPageChange={onPageChange}
            translation={{
              info: t('table.info'),
              empty: t('table.empty'),
            }}
            select={{ type: 'multiple', matcher: 'id' }}
            selected={selectedTerminal}
          />
        </SectionBody>
      </Section>
    </>
  );
};

const TerminalListFilter = ({ listParameters, setListParameters, close }) => {
  const { t: trans } = useTranslation();
  const [searchData, setSearchData] = useState({
    ...listParameters.filters,
  });

  const handleChangeSearchKeyword = (key, value) => {
    setSearchData({ ...searchData, [key]: value });
  };

  const handleSearch = () => {
    setListParameters((previousParams) => {
      return {
        ...previousParams,
        page: 1,
        filters: searchData,
      };
    });
    close();
  };

  const handleReset = () => {
    setSearchData({ outletName: '', nameExtension: '' });
  };

  return (
    <>
      <TitleModal.Header handleClose={close}>
        {trans('Filter')}
      </TitleModal.Header>
      <TitleModal.Body noPaddingBottom>
        <Grid columns={2}>
          <Column desktop={1}>
            <FormItem>
              <Label htmlFor='outletName'>{trans('OutletName')}</Label>
              <Input
                type='text'
                id='outletName'
                value={searchData.outletName}
                onChange={(event) => {
                  handleChangeSearchKeyword('outletName', event.target.value);
                }}
              />
            </FormItem>
          </Column>
          <Column desktop={1}>
            <FormItem>
              <Label htmlFor='nameExtension'>{trans('NameExtension')}</Label>
              <Input
                type='text'
                id='nameExtension'
                value={searchData.nameExtension}
                onChange={(event) => {
                  handleChangeSearchKeyword(
                    'nameExtension',
                    event.target.value,
                  );
                }}
              />
            </FormItem>
          </Column>
        </Grid>
      </TitleModal.Body>
      <TitleModal.Footer>
        <ButtonGroup>
          <Button danger onClick={handleReset}>
            {trans('button:Reset')}
          </Button>
          <Button danger onClick={close}>
            {trans('button:Cancel')}
          </Button>
          <Button success onClick={handleSearch}>
            {trans('button:Search')}
          </Button>
        </ButtonGroup>
      </TitleModal.Footer>
    </>
  );
};

const InputDropdown = ({
  title,
  checked,
  options,
  setOptions,
  toggleCheckbox,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [input, setInput] = useState('');

  const handleInputKeyDown = (event) => {
    const trimmedInput = input.trim();
    if (event.key === 'Enter' && trimmedInput) {
      const isExist = options.some((option) => {
        return option.text === trimmedInput;
      });
      if (!isExist) {
        setOptions((prev) => {
          return [...prev, { checked: true, text: trimmedInput }];
        });
        setInput('');
      }
    }
  };

  return (
    <Dropdown>
      <Dropdown.Toggle>
        <Button secondary style={{ height: '52px' }}>
          {t(title)}&nbsp;
          {checked.length > 0 && <BadgeFill>{checked.length}</BadgeFill>}
          <TiArrowSortedDown />
        </Button>
      </Dropdown.Toggle>
      <Dropdown.Content
        onShow={() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }}
      >
        <DropdownDivOption>
          <Input
            autoFocus
            backgroundReverse
            style={{ transition: '0s' }}
            id={title}
            placeholder={t(title)}
            type='text'
            value={input}
            onChange={(event) => {
              setInput(event.target.value);
            }}
            onKeyDown={handleInputKeyDown}
            ref={inputRef}
          />
        </DropdownDivOption>
        {options.map((option, index) => {
          return (
            <DropdownDivOption
              key={index}
              onClick={(event) => {
                if (
                  event.target.nodeName !== 'svg' &&
                  event.target.nodeName !== 'path'
                ) {
                  toggleCheckbox(index, options, setOptions);
                }
              }}
            >
              <Checkbox
                noMargin
                text={option.text}
                checked={option.checked}
                onChange={() => {
                  toggleCheckbox(index, options, setOptions);
                }}
              />
              <RxCross1
                onClick={() => {
                  setOptions(
                    options.filter((item) => {
                      return item.text !== option.text;
                    }),
                  );
                }}
              />
            </DropdownDivOption>
          );
        })}
      </Dropdown.Content>
    </Dropdown>
  );
};

const SearchDropdown = ({
  title,
  checked,
  simpleList,
  options,
  setOptions,
  toggleCheckbox,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const handleSearch = (keyword) => {
    const trimmedKeyword = keyword.trim().toLowerCase();
    if (!trimmedKeyword) {
      setOptions(simpleList);
      return;
    }
    const filterOptions = simpleList.filter((list) => {
      return list.text.toLowerCase().includes(trimmedKeyword);
    });
    setOptions(filterOptions);
  };

  return (
    <Dropdown>
      <Dropdown.Toggle>
        <Button secondary style={{ height: '52px' }}>
          {t(title)}&nbsp;
          {checked.length > 0 && <BadgeFill>{checked.length}</BadgeFill>}
          <TiArrowSortedDown />
        </Button>
      </Dropdown.Toggle>
      <Dropdown.Content
        onShow={() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }}
      >
        <DropdownDivOption>
          <Searchbox border onSearch={handleSearch} ref={inputRef} />
        </DropdownDivOption>
        {options.map((option, index) => {
          return (
            <DropdownDivOption
              key={index}
              onClick={() => {
                toggleCheckbox(index, options, setOptions);
              }}
            >
              <Checkbox
                noMargin
                text={option.text}
                checked={option.checked}
                onChange={() => {
                  toggleCheckbox(index, options, setOptions);
                }}
              />
            </DropdownDivOption>
          );
        })}
      </Dropdown.Content>
    </Dropdown>
  );
};

export default TerminalList;
